import { memo, useCallback, useMemo, useRef, useState } from "react";
import Tag from "../../Tag";
import Input from "..";
import DropDownSelection from "../../DropdownSelection";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { KEY } from "../../../data/variables";
import Icon from "../../Icon";
import { ICON } from "../../Icon/data";
import { useTranslation } from "react-i18next";

type CheckboxMultiselectType = {
  value: string[] | number[]
  options: { [key: string | number]: string }
  onChange: (val: string[] | number[]) => void
  id: string
  showSelectAll: boolean
  selectAllLabel?: string
  placeholder?: string
}

const CheckboxMultiselect = memo(({
  value = [], options = {}, onChange, id, showSelectAll = false, selectAllLabel,
  placeholder,
}: CheckboxMultiselectType) => {
  const dropdownRef = useRef()
  const inputRef = useRef()
  const searchRef = useRef()

  const { t } = useTranslation()

  const [search, setSearch] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const addValue = useCallback((key: string | number) => {
    const newValue = [...value]
    if (value.includes(key)) {
      const idx = value.indexOf(key)
      newValue.splice(idx, 1)
    } else newValue.push(key)
    
    onChange(newValue)
    setSearch('')
  }, [value, onChange])

  const selectAll = useCallback(() => {
    onChange(Object.keys(options))
    setSearch('')
  }, [onChange])

  const onKeyDown = useCallback((e: KeyboardEvent, id: string | number) => {
    if (e.key === KEY.ENTER) {
      if (id === 'SELECT_ALL') selectAll()
      else addValue(id)
    }
  }, [addValue])

  const filteredOptions = useMemo(() => {
    return Object.keys(options).reduce((arr, key, idx) => {
      if (idx === 0 && showSelectAll && !search) {
        const temp = {
          label: selectAllLabel ?? t('Select all'),
          onClick: () => selectAll(),
          onKeyDown: (e) => onKeyDown(e, 'SELECT_ALL')
        }
        arr.push(temp)
      }

      const label = options[key]
      const labelElem = (
        <div className="Multiselect__checkbox-option">
          <Input
            type="checkbox"
            value={value.includes(key) || value.includes(Number(key))}
            onChange={() => addValue(key)}
            label={label}
          />
        </div>
      )

      if (search?.length) {
        if (label.toLowerCase().includes(search.toLowerCase())) {
          const temp = {
            label: labelElem,
            onClick: () => addValue(key),
            onKeyDown: (e: KeyboardEvent) => onKeyDown(e, key)
          }
          arr.push(temp)
        }
      } else {
        const temp = {
          label: labelElem,
          onClick: () => addValue(key),
          onKeyDown: (e) => onKeyDown(e, key)
        }
        arr.push(temp)
      }
      return arr
    }, [])
  }, [options, addValue, onKeyDown, search, value, showSelectAll])

  return (
    <div className="Multiselect" ref={inputRef} onClick={() => searchRef?.current?.focus()}>
      <div className="Multiselect__search">
        <div className={`Multiselect__search-tags${value.length ? '' : ' empty'}`}>
          {value?.length ?
            <Tag label={value.length === 1 ? options[value] : value.length} size="s" color="high-contrast" onClose={() => onChange([])} />
            : null}
        </div>
        <Input
          ref={searchRef}
          type="text"
          value={search}
          onInput={(val) => setSearch(val)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
        />
        <div className="Multiselect__search-icons">
          {value.length > 0 && (
            <div
              className="Multiselect__search-icons__remove"
              role="button"
              tabIndex={0}
              onClick={() => onChange([])}
            >
              <Icon icon={ICON.CLOSE} size={16} />
            </div>
          )}
          <Icon icon={isOpen ? ICON.CHEVRON_UP : ICON.CHEVRON_DOWN} size={16} />
        </div>
      </div>
      {isOpen && (
        <div ref={dropdownRef} className="Multiselect__dropdown">
          <DropDownSelection options={filteredOptions} boundsRef={inputRef} />
        </div>
      )}
    </div>
  )
})

export default CheckboxMultiselect
